import { CalendarDays, Check } from 'lucide-react';
import { useState } from 'react';

import { DatePicker } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { useFinancialYear } from '@/hooks/useCompanySettings';
import { type DashRange, fytdRange, presetRange } from '@/lib/sales-metrics';
import { FY_DEFAULT } from '@/services/companySettings.service';
import cn from '@/utils/cn';

type Mode = 'all' | 'month' | 'year' | 'fytd' | 'custom';

const pad = (n: number): string => String(n).padStart(2, '0');
const toInput = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmt = (d: Date): string =>
  d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

interface DashboardFilterProps {
  range: DashRange;
  onChange: (r: DashRange) => void;
}

const MODES: { key: Mode; label: string }[] = [
  { key: 'month', label: 'This Month' },
  { key: 'year', label: 'This Year' },
  { key: 'fytd', label: 'FYTD' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

export function DashboardFilter({ range, onChange }: DashboardFilterProps) {
  const [mode, setMode] = useState<Mode>('month');
  const [from, setFrom] = useState(toInput(range.from));
  const [to, setTo] = useState(toInput(range.to));
  const { data: fy } = useFinancialYear();

  const pick = (m: Mode) => {
    setMode(m);
    if (m === 'all') onChange(presetRange('all'));
    if (m === 'month') onChange(presetRange('month'));
    if (m === 'year') onChange(presetRange('year'));
    if (m === 'fytd') {
      onChange(
        fytdRange(fy?.startMonth ?? FY_DEFAULT.startMonth, fy?.startDay ?? FY_DEFAULT.startDay),
      );
    }
  };

  const applyCustom = () => {
    const f = new Date(from);
    const t = new Date(to);
    if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime()) || f > t) return;
    onChange({ from: f, to: t, label: `${fmt(f)} - ${fmt(t)}` });
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Filter</span>
        </div>

        <div className="flex w-full flex-wrap items-center gap-1 rounded-lg border border-border bg-background p-1 sm:w-auto">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => pick(m.key)}
              className={cn(
                'min-h-11 rounded-md px-3 py-1 text-[11px] font-semibold transition-colors sm:min-h-0 sm:px-2.5',
                mode === m.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>

        {mode === 'custom' && (
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <DatePicker value={from} onChange={setFrom} className="w-full sm:w-40" />
            <span className="text-xs text-muted-foreground">to</span>
            <DatePicker value={to} onChange={setTo} className="w-full sm:w-40" />
            <Button size="sm" onClick={applyCustom} className="h-9 gap-1">
              <Check className="h-3.5 w-3.5" />
              Apply
            </Button>
          </div>
        )}

        <div className="ml-auto flex items-center gap-1.5 rounded-md bg-secondary/60 px-3 py-1.5">
          <span className="text-xs text-muted-foreground">Showing</span>
          <span className="text-xs font-semibold text-foreground">{range.label}</span>
        </div>
      </div>
    </div>
  );
}
